import React, { useState } from 'react';
import { 
  Box, Button, Typography, Grid, Paper, Select, MenuItem, 
  FormControl, InputLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Alert,
  CircularProgress
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import * as XLSX from 'xlsx';
import axios from 'axios';

// The exact fields the backend expects
const requiredFields = [
  { key: 'staff_code', label: 'Staff Code (Person ID)', required: true },
  { key: 'employee_number', label: 'Employee Number (Payroll ID)', required: false },
  { key: 'full_name', label: 'Full Name', required: true },
  { key: 'nrc_number', label: 'NRC Number', required: true },
  { key: 'date_of_birth', label: 'Date of Birth', required: false },
  { key: 'gender', label: 'Gender', required: false },
  { key: 'marital_status', label: 'Marital Status', required: false },
  { key: 'race', label: 'Race', required: false },
  { key: 'religion', label: 'Religion', required: false },
  { key: 'nationality', label: 'Nationality', required: false },
  { key: 'record_date', label: 'Record/Event Date', required: true },
  { key: 'record_type', label: 'Record Type (HIRE/PROM/CESS)', required: true },
  { key: 'company', label: 'Company (SOM Node)', required: false },
  { key: 'division', label: 'Division (SOM Node)', required: false },
  { key: 'department', label: 'Department (SOM Node)', required: false },
  { key: 'section', label: 'Section (SOM Node)', required: false },
  { key: 'unit', label: 'Unit (SOM Node)', required: false },
  { key: 'branch', label: 'Branch', required: false },
  { key: 'job_title', label: 'Job Title', required: false },
  { key: 'grade', label: 'Grade', required: false },
  { key: 'basic_salary', label: 'Basic Salary', required: false },
];

export default function Step1Upload({ onSuccess, loading, setLoading }) {
  const [file1, setFile1] = useState(null);
  const [file2, setFile2] = useState(null);
  
  const [file1Headers, setFile1Headers] = useState([]);
  const [file2Headers, setFile2Headers] = useState([]);
  
  const [file1Data, setFile1Data] = useState([]);
  const [file2Data, setFile2Data] = useState([]);

  // Mapping state: fieldKey -> { file: 'file1' | 'file2', header: 'Source Header' }
  const [mapping, setMapping] = useState({});
  const [joinKey1, setJoinKey1] = useState('');
  const [joinKey2, setJoinKey2] = useState('');
  const [error, setError] = useState('');

  const handleFileUpload = (e, fileNum) => {
    const file = e.target.files[0];
    if (!file) return;

    if (fileNum === 1) setFile1(file);
    else setFile2(file);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: 'binary', raw: true });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        
        // Extract headers explicitly from the first row
        const headerRow = XLSX.utils.sheet_to_json(ws, { header: 1 })[0];
        const headers = headerRow ? headerRow.filter(h => h) : [];
        
        // Extract data with default values so keys aren't missing
        const data = XLSX.utils.sheet_to_json(ws, { defval: '' });
        
        if (data.length > 0) {
          if (fileNum === 1) {
            setFile1Headers(headers);
            setFile1Data(data);
          } else {
            setFile2Headers(headers);
            setFile2Data(data);
          }
        }
      } catch (err) {
        setError('Error reading Excel file: ' + err.message);
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleMappingChange = (fieldKey, value) => {
    // value looks like "file1::Header Name"
    if (!value) {
      const newMap = { ...mapping };
      delete newMap[fieldKey];
      setMapping(newMap);
      return;
    }
    const [fileSrc, header] = value.split('::');
    setMapping({ ...mapping, [fieldKey]: { file: fileSrc, header } });
  };

  const validateAndSubmit = async () => {
    setError('');
    // Check required fields
    for (const field of requiredFields) {
      if (field.required && !mapping[field.key]) {
        setError(`Mapping required for: ${field.label}`);
        return;
      }
    }

    if (file2 && (!joinKey1 || !joinKey2)) {
      setError('You uploaded two files. You must select the Join Keys to link them together.');
      return;
    }

    setLoading(true);

    try {
      // Create normalized records array
      const normalizedRecords = [];

      // If we have 2 files, we join them first based on the join keys
      // Otherwise we just iterate file 1
      if (file2) {
        // Create lookup for file 2
        const file2Map = {};
        file2Data.forEach(row => {
          if (row[joinKey2]) file2Map[row[joinKey2]] = row;
        });

        file1Data.forEach(row1 => {
          const matchedValue = row1[joinKey1];
          const row2 = matchedValue ? file2Map[matchedValue] : {};
          
          const combinedRow = {};
          // Apply mapping
          Object.keys(mapping).forEach(destKey => {
            const src = mapping[destKey];
            if (src.file === 'file1') combinedRow[destKey] = row1[src.header];
            if (src.file === 'file2') combinedRow[destKey] = row2 ? row2[src.header] : null;
          });
          normalizedRecords.push(combinedRow);
        });
      } else {
        // Only file 1
        file1Data.forEach(row1 => {
          const combinedRow = {};
          Object.keys(mapping).forEach(destKey => {
            const src = mapping[destKey];
            if (src.file === 'file1') combinedRow[destKey] = row1[src.header];
          });
          normalizedRecords.push(combinedRow);
        });
      }

      // Send to backend
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const tenant = localStorage.getItem('tenant') || '';
      
      const response = await axios.post(`${process.env.REACT_APP_API_URL1}/historical-import/validate`, {
        records: normalizedRecords
      }, {
        withCredentials: true,
        headers: { 
          Authorization: `Bearer ${token}`,
          'X-Tenant': tenant
        }
      });

      if (response.data.success) {
        onSuccess(response.data.data);
      } else {
        setError(response.data.message || 'Validation failed');
      }

    } catch (err) {
      setError(err.response?.data?.message || err.message || 'An error occurred during validation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>1. Upload Files</Typography>
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="subtitle1" fontWeight="bold">File 1 (Employment History)</Typography>
            <Typography variant="body2" color="textSecondary" mb={2}>Contains event rows (HIRE, PROM, CESS) and roles.</Typography>
            <Button component="label" variant="contained" startIcon={<CloudUploadIcon />}>
              Upload File 1
              <input type="file" hidden accept=".xlsx,.xls,.csv" onChange={(e) => handleFileUpload(e, 1)} />
            </Button>
            {file1 && <Typography mt={1} color="success.main">{file1.name} loaded</Typography>}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="subtitle1" fontWeight="bold">File 2 (Personal Data) - Optional</Typography>
            <Typography variant="body2" color="textSecondary" mb={2}>If personal details are in a separate file.</Typography>
            <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />}>
              Upload File 2
              <input type="file" hidden accept=".xlsx,.xls,.csv" onChange={(e) => handleFileUpload(e, 2)} />
            </Button>
            {file2 && <Typography mt={1} color="success.main">{file2.name} loaded</Typography>}
          </Paper>
        </Grid>
      </Grid>

      {file1Headers.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>2. Dynamic Column Mapping</Typography>
          <Typography variant="body2" color="textSecondary" mb={2}>
            Match your file columns to PeopleHub fields. Required fields are marked with *.
          </Typography>

          {file2 && (
            <Paper sx={{ p: 2, mb: 3, bgcolor: '#f8f9fa' }}>
              <Typography variant="subtitle2" gutterBottom>How should we join these two files?</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Join Key in File 1</InputLabel>
                    <Select value={joinKey1} onChange={e => setJoinKey1(e.target.value)} label="Join Key in File 1">
                      {file1Headers.map(h => <MenuItem key={h} value={h}>{h}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Join Key in File 2</InputLabel>
                    <Select value={joinKey2} onChange={e => setJoinKey2(e.target.value)} label="Join Key in File 2">
                      {file2Headers.map(h => <MenuItem key={h} value={h}>{h}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>
          )}

          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><strong>PeopleHub Field</strong></TableCell>
                  <TableCell><strong>Map to Column</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requiredFields.map(field => (
                  <TableRow key={field.key}>
                    <TableCell>
                      {field.label} {field.required && <span style={{color:'red'}}>*</span>}
                    </TableCell>
                    <TableCell>
                      <FormControl fullWidth size="small">
                        <Select
                          displayEmpty
                          value={mapping[field.key] ? `${mapping[field.key].file}::${mapping[field.key].header}` : ''}
                          onChange={(e) => handleMappingChange(field.key, e.target.value)}
                        >
                          <MenuItem value=""><em>-- Ignore --</em></MenuItem>
                          {file1Headers.length > 0 && <MenuItem disabled>--- File 1 Columns ---</MenuItem>}
                          {file1Headers.map(h => <MenuItem key={`1-${h}`} value={`file1::${h}`}>{h}</MenuItem>)}
                          
                          {file2Headers.length > 0 && <MenuItem disabled>--- File 2 Columns ---</MenuItem>}
                          {file2Headers.map(h => <MenuItem key={`2-${h}`} value={`file2::${h}`}>{h}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box textAlign="right">
            <Button 
              variant="contained" 
              color="primary" 
              onClick={validateAndSubmit}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading ? 'Validating...' : 'Validate Mapping'}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
